import { ChatMessage } from "../models/ChatMessage";
import { ModelOutputMessageData } from "../models/message-data/ModelOutputMessageData";
import { FunctionResultMessageData } from "../models/message-data/FunctionResultMessageData";
import { MessageProcessor } from "../models/MessageDataProcessor";
import ModelOutputMessage from "./messages/ModelOutputMessage";
import { cn } from "@/lib/utils";
import {
  Loader2,
  CheckCircle2,
  AlertCircle,
  ChevronDown,
  Lightbulb,
} from "lucide-react";
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from "@/components/ui/collapsible";
import { Button } from "@/components/ui/button";
import { useState } from "react";
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import ScrollableText from "@/shared/components/scrollable-text/ScrollableText";
import { MessageRole } from "../enums/MessageRole";

interface ExecutionPlanProps {
  messages: ChatMessage[];
  isFinished: boolean;
}

const functionCallTitles = new Map<string, string>([
  ["filter_documents", "Filtering documents"],
  ["search_companies", "Searching for companies"],
  ["ask_documents", "Reading through documents"],
  ["get_estimates", "Getting estimates"],
  ["calculate", "Crunching numbers"],
  ["search_web", "Searching the web"],
]);

export function ExecutionPlanNew({ messages, isFinished }: ExecutionPlanProps) {
  const [isOpen, setIsOpen] = useState(false);
  const finishedTitle = "Research completed";
  const workingTitle = "Researching...";
  const accordionTitle = isFinished ? finishedTitle : workingTitle;

  return (
    <Collapsible
      open={isOpen}
      onOpenChange={setIsOpen}
      className="border rounded-lg bg-muted/50 font-mono"
    >
      <CollapsibleTrigger asChild>
        <Button
          variant="ghost"
          className="w-full justify-between p-1.5 hover:no-underline font-normal border-b"
        >
          <span className="text-sm">{accordionTitle}</span>
          <ChevronDown
            className={cn(
              "h-4 w-4 transition-transform duration-200",
              isOpen && "transform rotate-180",
            )}
          />
        </Button>
      </CollapsibleTrigger>
      <CollapsibleContent className="px-4 py-2">
        <div className="space-y-1.5">
          {messages.map((message) => {
            if (message.role !== MessageRole.Assistant) return null;

            const steps = [];
            const data = message.data as ModelOutputMessageData;
            const functionCalls = data.functionCalls!;
            const isLoading = message.finishedAt === undefined;

            // Model output step
            steps.push(
              <div key={message.id} className="relative pl-6">
                <div className="pb-1.5">
                  <div className="absolute left-0 -translate-x-[11px] flex h-6 w-6 items-center justify-center">
                    {isLoading ? (
                      <div className="bg-muted/50 rounded-full p-1">
                        <Loader2 className="h-4 w-4 animate-spin text-muted-foreground" />
                      </div>
                    ) : (
                      <div className="bg-muted/50 rounded-full p-1">
                        <CheckCircle2 className="h-4 w-4 text-primary" />
                      </div>
                    )}
                  </div>
                  <div className="space-y-0.5">
                    <div className="text-sm">Model output</div>
                    <div className="text-sm text-muted-foreground space-y-0.5">
                      <ModelOutputMessage data={data} />
                      {data.thinking && (
                        <Popover modal={false}>
                          <PopoverTrigger>
                            <Tooltip delayDuration={100}>
                              <TooltipTrigger>
                                <Lightbulb className="h-4 w-4" />
                              </TooltipTrigger>
                              <TooltipContent side="bottom">
                                Reasoning
                              </TooltipContent>
                            </Tooltip>
                          </PopoverTrigger>
                          <PopoverContent className="w-[500px] h-full rounded-md">
                            <ScrollableText content={data.thinking} />
                          </PopoverContent>
                        </Popover>
                      )}
                    </div>
                  </div>
                </div>
              </div>,
            );

            // Function call steps
            functionCalls.forEach((call) => {
              const functionCallMessage = messages.find(
                (m) =>
                  (m.data as FunctionResultMessageData)?.functionCall
                    ?.functionCall?.id === call.id,
              );
              const isLoading = functionCallMessage?.finishedAt === undefined;
              const hasFailed =
                functionCallMessage?.errorMessage !== undefined &&
                functionCallMessage.errorMessage !== null;

              steps.push(
                <div key={call.id} className="relative pl-6">
                  <div className="pb-1.5">
                    <div className="absolute left-0 -translate-x-[11px] flex h-6 w-6 items-center justify-center">
                      {isLoading ? (
                        <div className="bg-muted/50 rounded-full p-1">
                          <Loader2 className="h-4 w-4 animate-spin text-muted-foreground" />
                        </div>
                      ) : hasFailed ? (
                        <div className="bg-muted/50 rounded-full p-1">
                          <AlertCircle className="h-4 w-4 text-muted-foreground" />
                        </div>
                      ) : (
                        <div className="bg-muted/50 rounded-full p-1">
                          <CheckCircle2 className="h-4 w-4 text-primary" />
                        </div>
                      )}
                    </div>
                    <div className="space-y-0.5">
                      <div
                        className={cn(
                          "text-sm",
                          hasFailed && "text-muted-foreground",
                        )}
                      >
                        {functionCallTitles.get(call.name)}
                      </div>
                      {functionCallMessage?.data && (
                        <div className="text-sm text-muted-foreground">
                          {MessageProcessor.GetComponent(
                            functionCallMessage.role,
                            functionCallMessage.data,
                            isLoading,
                          )}
                        </div>
                      )}
                      {hasFailed && functionCallMessage?.errorMessage && (
                        <div className="text-sm text-muted-foreground">
                          {functionCallMessage.errorMessage}
                        </div>
                      )}
                    </div>
                  </div>
                </div>,
              );
            });

            return steps;
          })}
        </div>
      </CollapsibleContent>
    </Collapsible>
  );
}
